import { useCallback, useEffect, useState } from 'react'
import { fetchCustomerPrivacyDashboard, type CustomerPrivacyDashboard } from '../../customerPrivacyApi'
import { toPrivacyErrorMessage } from './privacyErrors'

type LoadMode = 'initial' | 'refresh'

type UsePrivacyDashboardStateInput = {
  canLoad: boolean
}

export const usePrivacyDashboardState = ({ canLoad }: UsePrivacyDashboardStateInput) => {
  const [privacyDashboard, setPrivacyDashboard] = useState<CustomerPrivacyDashboard | null>(null)
  const [privacyLoading, setPrivacyLoading] = useState(false)
  const [privacyRefreshing, setPrivacyRefreshing] = useState(false)
  const [privacyError, setPrivacyError] = useState('')
  const [privacyNotice, setPrivacyNotice] = useState('')

  const loadDashboard = useCallback(
    (mode: LoadMode) => {
      if (mode === 'initial') {
        setPrivacyLoading(true)
      } else {
        setPrivacyRefreshing(true)
      }
      setPrivacyError('')
      void fetchCustomerPrivacyDashboard()
        .then((payload) => {
          setPrivacyDashboard(payload)
        })
        .catch((error) => {
          setPrivacyError(toPrivacyErrorMessage(error))
        })
        .finally(() => {
          if (mode === 'initial') {
            setPrivacyLoading(false)
          } else {
            setPrivacyRefreshing(false)
          }
        })
    },
    [],
  )

  useEffect(() => {
    if (!canLoad) {
      setPrivacyDashboard(null)
      setPrivacyLoading(false)
      setPrivacyRefreshing(false)
      setPrivacyError('')
      setPrivacyNotice('')
      return
    }
    loadDashboard('initial')
  }, [canLoad, loadDashboard])

  useEffect(() => {
    if (!canLoad || !privacyDashboard?.exportState.requestId) return
    if (privacyDashboard.exportState.state !== 'processing') return
    const timer = window.setInterval(() => {
      loadDashboard('refresh')
    }, 5_000)
    return () => {
      window.clearInterval(timer)
    }
  }, [canLoad, loadDashboard, privacyDashboard?.exportState.requestId, privacyDashboard?.exportState.state])

  const refreshPrivacyDashboard = () => {
    if (!canLoad) return
    loadDashboard('refresh')
  }

  return {
    privacyDashboard,
    privacyLoading,
    privacyRefreshing,
    privacyError,
    setPrivacyError,
    privacyNotice,
    setPrivacyNotice,
    loadDashboard,
    refreshPrivacyDashboard,
  }
}
